import { lazy, type ComponentType } from 'react';

const RELOADED_KEY = 'staleChunkReloaded';
const RELOADED_PAGE_GUARD_MS = 60_000;

// After a deploy, an open tab still references the previous build's hashed chunks; Hosting answers them with
// `index.html`, so the dynamic import fails. Reloading picks up the new build. The marker lives for one navigation:
// the page load the reload starts consumes it, and failures in that page's first minute surface normally instead of
// looping; after that the page reloads like any other, so a later deploy is picked up too. One import can fail
// several times (each CSS dependency plus the module itself), so the decision is made per page load.
export function installStaleChunkReload() {
  const isReloadedPage = readSessionItem(RELOADED_KEY) !== null;
  const loadedAt = Date.now();
  removeSessionItem(RELOADED_KEY);

  window.addEventListener('vite:preloadError', (event) => {
    if (isReloadedPage && Date.now() - loadedAt < RELOADED_PAGE_GUARD_MS) return;
    // Without a stored marker the reloaded page could not tell it already reloaded, so it would loop.
    if (!writeSessionItem(RELOADED_KEY, '1')) return;

    event.preventDefault();
    window.location.reload();
  });
}

// A prevented preload error resolves the import to `undefined`; staying suspended keeps the fallback up until the
// reload replaces the page.
export function lazyPage<K extends string, M extends Record<K, ComponentType>>(load: () => Promise<M | undefined>, name: K) {
  return lazy(async () => {
    const module = await load();
    if (!module) return new Promise<never>(() => {});

    return { default: module[name] };
  });
}

function readSessionItem(key: string) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function removeSessionItem(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

function writeSessionItem(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
